import React, { useEffect, useMemo, useState } from "react";
import { Card, Form, Input, Button, Typography, Tag, Switch } from "antd";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import subscriptionService from "../services/subscriptionService";
import authService from "../services/authService";

const { Title, Text } = Typography;

const Settings = () => {
  const { user, refreshUser } = useAuth();
  const [subLoading, setSubLoading] = useState(false);
  const [manageSubLoading, setManageSubLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [form] = Form.useForm();
  const isAdmin = user?.role === "admin";

  const authToken = useMemo(() => {
    const authData = JSON.parse(localStorage.getItem("authData") || "{}");
    return authData?.token || null;
  }, []);

  const subscriptionColorMap = {
    active: "green",
    inactive: "default",
    past_due: "orange",
    cancelled: "red",
  };

  const canManageSubscription = !!user?.stripeSubscriptionId;

  useEffect(() => {
    const syncSubscription = async () => {
      if (!authToken) return;
      if (isAdmin) return;
      if (!user?.stripeSubscriptionId && !user?.isSubscribed) return;

      try {
        setSubLoading(true);
        await subscriptionService.syncSubscriptionStatus(authToken);
      } catch (error) {
        // Non-blocking; we'll still show the cached auth state.
      } finally {
        await refreshUser().catch(() => {});
        setSubLoading(false);
      }
    };

    syncSubscription();
  }, [authToken, isAdmin, refreshUser, user?.isSubscribed, user?.stripeSubscriptionId]);

  const formatDate = (value) => {
    if (!value) return "N/A";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "N/A";
    return parsed.toLocaleDateString();
  };

  const handleToggleAutoRenew = async (checked) => {
    if (!authToken) {
      toast.error("Session expired. Please login again.");
      return;
    }

    try {
      setManageSubLoading(true);
      await subscriptionService.updateAutoRenew(authToken, checked);
      await refreshUser();
      toast.success(checked ? "Auto-renew enabled." : "Auto-renew disabled.");
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Unable to update auto-renew.";
      toast.error(message);
    } finally {
      setManageSubLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!authToken) {
      toast.error("Session expired. Please login again.");
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to cancel your subscription now? This action cannot be undone."
    );
    if (!confirmed) return;

    try {
      setManageSubLoading(true);
      const response = await subscriptionService.cancelSubscription(authToken);
      await refreshUser();
      toast.success(
        response?.message ||
          "Cancellation scheduled. Subscription remains active until period end."
      );
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Unable to cancel subscription.";
      toast.error(message);
    } finally {
      setManageSubLoading(false);
    }
  };

  const handleChangePassword = async (values) => {
    if (!authToken) {
      toast.error("Session expired. Please login again.");
      return;
    }

    if (values.newPassword !== values.confirmPassword) {
      toast.error("New password and confirm password do not match.");
      return;
    }

    try {
      setPasswordLoading(true);
      await authService.changePassword(authToken, {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      toast.success("Password changed successfully.");
      form.resetFields();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Unable to change password right now.";
      toast.error(message);
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <section className="bg-white min-h-[calc(100vh-90px)] px-10 py-8">
      <div className="mb-8">
        <Title level={3} className="!m-0 !text-[#1c5a85] font-semibold">
          Settings
        </Title>
      </div>

      <Card className="mb-[576px] rounded-xl border border-[#e8f1f7]" title="Change Password">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleChangePassword}
          className="max-w-xl"
          requiredMark={false}
        >
          <Form.Item
            label="Current Password"
            name="currentPassword"
            rules={[{ required: true, message: "Please enter your current password." }]}
          >
            <Input.Password placeholder="Enter current password" size="large" />
          </Form.Item>

          <Form.Item
            label="New Password"
            name="newPassword"
            rules={[
              { required: true, message: "Please enter a new password." },
              { min: 4, message: "New password must be at least 4 characters." },
            ]}
          >
            <Input.Password placeholder="Enter new password" size="large" />
          </Form.Item>

          <Form.Item
            label="Confirm New Password"
            name="confirmPassword"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: "Please confirm your new password." },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match."));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm new password" size="large" />
          </Form.Item>

          <Button
            type="primary"
            htmlType="submit"
            loading={passwordLoading}
            className="bg-[#1c5a85]"
          >
            Update Password
          </Button>
        </Form>
      </Card>

      {!isAdmin && (
        <Card
          className="rounded-xl border border-[#e8f1f7]"
          loading={subLoading}
          title="Subscription"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Text className="text-gray-500 text-sm block">Plan</Text>
              <Text strong>{user?.plan ? "Premium Monthly" : "No active plan"}</Text>
            </div>
            <div>
              <Text className="text-gray-500 text-sm block">Status</Text>
              <Tag color={subscriptionColorMap[user?.subscriptionStatus] || "default"}>
                {(user?.subscriptionStatus || "inactive").toUpperCase()}
              </Tag>
            </div>
            <div>
              <Text className="text-gray-500 text-sm block">Subscription Start Date</Text>
              <Text>{formatDate(user?.currentPeriodStart)}</Text>
            </div>
            <div>
              <Text className="text-gray-500 text-sm block">Subscription End Date</Text>
              <Text>{formatDate(user?.currentPeriodEnd)}</Text>
            </div>
            <div>
              <Text className="text-gray-500 text-sm block">Auto Renew</Text>
              <Switch
                checked={!!user?.autoRenew}
                onChange={handleToggleAutoRenew}
                loading={manageSubLoading}
                disabled={!canManageSubscription || user?.subscriptionStatus === "cancelled"}
                checkedChildren="On"
                unCheckedChildren="Off"
              />
            </div>
            <div>
              <Text className="text-gray-500 text-sm block">Cancel at Period End</Text>
              <Text>{user?.cancelAtPeriodEnd ? "Yes" : "No"}</Text>
            </div>
            <div className="md:col-span-2">
              <Button
                danger
                onClick={handleCancelSubscription}
                loading={manageSubLoading}
                disabled={!canManageSubscription || user?.subscriptionStatus === "cancelled"}
              >
                Cancel At Period End
              </Button>
            </div>
          </div>
        </Card>
      )}
    </section>
  );
};

export default Settings;
